import React, { useState, useEffect } from 'react';
import {
  Headphones,
  Volume2,
  Mic,
  RotateCcw,
  Check,
  CheckCircle2,
  XCircle,
  Play,
  Sparkles
} from 'lucide-react';
import { SessionGameRound, SessionPlayerState } from '../../../types/session-games';
import { useSpeechSynthesis } from '../../../hooks/useSpeechSynthesis';
import { validateSentenceAnswer } from '../../../services/sessionGameService';
import { playCorrectSound, playWrongSound } from '../../../utils/soundEffects';

interface AudioMysteryViewProps {
  round: SessionGameRound;
  myPlayer: SessionPlayerState;
  partnerPlayer?: SessionPlayerState;
  onSubmit: (result: {
    answer: string;
    isCorrect: boolean;
    points: number;
  }) => void;
  isRoundResult: boolean;
}

export const AudioMysteryView: React.FC<AudioMysteryViewProps> = ({
  round,
  myPlayer,
  partnerPlayer,
  onSubmit,
  isRoundResult
}) => {
  const { speak } = useSpeechSynthesis({ lang: 'en-US' });
  const [wordBank, setWordBank] = useState<string[]>([]);
  const [assembled, setAssembled] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);

  useEffect(() => {
    if (round.scrambledWords) {
      setWordBank([...round.scrambledWords]);
    } else {
      setWordBank(round.correctAnswer.split(' ').sort(() => Math.random() - 0.5));
    }
    setAssembled([]);
    setSubmitted(false);
    setHasPlayed(false);
    setFeedback(null);
  }, [round.id]);

  const playAudio = (rate = 1.0) => {
    speak(round.targetText, 'en-US', rate);
    setHasPlayed(true);
  };

  const handleWordSelect = (word: string, indexInBank: number) => {
    if (submitted || isRoundResult) return;
    setAssembled((prev) => [...prev, word]);
    setWordBank((prev) => prev.filter((_, idx) => idx !== indexInBank));
    setFeedback(null);
  };

  const handleWordRemove = (word: string, indexInAssembled: number) => {
    if (submitted || isRoundResult) return;
    setWordBank((prev) => [...prev, word]);
    setAssembled((prev) => prev.filter((_, idx) => idx !== indexInAssembled));
    setFeedback(null);
  };

  const handleReset = () => {
    if (submitted || isRoundResult) return;
    if (round.scrambledWords) {
      setWordBank([...round.scrambledWords]);
    } else {
      setWordBank(round.correctAnswer.split(' ').sort(() => Math.random() - 0.5));
    }
    setAssembled([]);
    setFeedback(null);
  };

  const handleCheck = () => {
    if (assembled.length === 0 || submitted) return;
    const isCorrect = validateSentenceAnswer(round.correctAnswer, assembled);

    if (isCorrect) {
      playCorrectSound();
      setFeedback('correct');
      setSubmitted(true);
      onSubmit({
        answer: assembled.join(' '),
        isCorrect: true,
        points: 20
      });
    } else {
      playWrongSound();
      setFeedback('incorrect');
    }
  };

  return (
    <div className="flex flex-col items-center justify-between h-full max-w-2xl mx-auto space-y-6">
      {/* Listening Mask Card */}
      <div className="w-full bg-white rounded-3xl p-6 md:p-8 border border-pink-100 shadow-md text-center">
        <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-pink-600 uppercase tracking-wider mb-3">
          <Headphones className="w-4 h-4" />
          <span>Audio Mystery • Listen & Reconstruct</span>
        </div>

        <div className="my-5 flex flex-col items-center justify-center gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => playAudio(1.0)}
              className="px-5 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold text-sm shadow-md hover:scale-105 active:scale-95 transition-all flex items-center gap-2"
            >
              <Volume2 className="w-5 h-5" />
              <span>Listen (1.0x)</span>
            </button>

            <button
              onClick={() => playAudio(0.75)}
              className="px-4 py-3 rounded-2xl border border-pink-200 bg-pink-50 text-pink-700 hover:bg-pink-100 font-bold text-xs transition-all flex items-center gap-1.5"
            >
              <span>Slow (0.75x)</span>
            </button>
          </div>

          <p className="text-xs text-gray-400">
            {hasPlayed ? 'Audio played. Assemble the words you heard below!' : 'Tap to listen to the secret audio!'}
          </p>
        </div>

        {/* Revealed target on result */}
        {(isRoundResult || submitted) && (
          <div className="mt-4 pt-3 border-t border-gray-100 animate-in fade-in">
            <p className="text-base font-bold text-gray-900 leading-snug">
              "{round.targetText}"
            </p>
            {round.translation && (
              <p className="text-xs text-pink-700 mt-1 italic">{round.translation}</p>
            )}
          </div>
        )}
      </div>

      {/* Assembly Bar */}
      <div className="w-full min-h-[90px] bg-pink-50/40 rounded-2xl p-4 border-2 border-dashed border-pink-300 flex flex-wrap items-center justify-center gap-2">
        {assembled.length === 0 ? (
          <p className="text-xs text-pink-400 font-medium">
            Tap the word blocks below in the exact order you heard them
          </p>
        ) : (
          assembled.map((word, idx) => (
            <button
              key={`${word}_${idx}`}
              onClick={() => handleWordRemove(word, idx)}
              disabled={submitted || isRoundResult}
              className="px-3.5 py-1.5 rounded-xl bg-pink-600 text-white font-bold text-xs md:text-sm shadow-xs hover:bg-pink-700 transition-all flex items-center gap-1"
            >
              <span>{word}</span>
              <span className="text-pink-200 text-xs">✕</span>
            </button>
          ))
        )}
      </div>

      {/* Feedback banner */}
      {feedback === 'correct' && (
        <div className="w-full bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl p-3 flex items-center justify-center gap-2 text-xs font-bold animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span>Great ears! 100% correct audio match. +20 points</span>
        </div>
      )}

      {feedback === 'incorrect' && (
        <div className="w-full bg-red-50 border border-red-200 text-red-800 rounded-2xl p-3 flex items-center justify-center gap-2 text-xs font-bold animate-in shake">
          <XCircle className="w-4 h-4 text-red-600" />
          <span>Not quite what was said. Try listening again with slow speed!</span>
        </div>
      )}

      {/* Word Bank */}
      <div className="w-full bg-white rounded-2xl p-4 border border-gray-100 shadow-xs">
        <div className="flex items-center justify-between pb-2 border-b border-gray-100 mb-2.5 text-xs text-gray-400 font-bold uppercase tracking-wider">
          <span>Words Heard:</span>
          <button
            onClick={handleReset}
            disabled={submitted || isRoundResult}
            className="flex items-center gap-1 text-gray-500 hover:text-pink-600 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Reset</span>
          </button>
        </div>

        <div className="flex flex-wrap gap-2 justify-center">
          {wordBank.map((word, idx) => (
            <button
              key={`${word}_${idx}`}
              onClick={() => handleWordSelect(word, idx)}
              disabled={submitted || isRoundResult}
              className="px-3 py-1.5 rounded-xl bg-gray-100 hover:bg-pink-100 hover:text-pink-700 text-gray-800 font-bold text-xs md:text-sm border border-gray-200 shadow-xs transition-all"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {/* Check Button */}
      {!submitted && !isRoundResult && (
        <button
          onClick={handleCheck}
          disabled={assembled.length === 0}
          className="w-full max-w-sm py-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-2xl font-black text-sm shadow-md hover:opacity-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4" />
          <span>Validate Audio Dictation</span>
        </button>
      )}

      {/* Partner Status Card */}
      {partnerPlayer && (
        <div className="w-full bg-white rounded-2xl p-3 border border-gray-100 shadow-xs flex items-center justify-between text-xs">
          <span className="font-bold text-gray-800">{partnerPlayer.userName}:</span>
          <span className={partnerPlayer.roundFinished ? 'text-emerald-600 font-bold' : 'text-gray-400'}>
            {partnerPlayer.roundFinished ? '✓ Audio Decoded!' : 'Listening closely...'}
          </span>
        </div>
      )}
    </div>
  );
};
